<template>
    <div class="active-chart-table-container"
        style="display: flex; flex-wrap: wrap; margin-left: 0; background-color: white; margin-left: 1em; border-radius: 8px; overflow-y: auto">
        <div class="chart-approval-only"
            style="height: 70vh;display: flex; flex-wrap: wrap; margin-left: 0; width: 100%; border-radius: 8px;"
            :style="loadingPatient || !patient || loadingResidentDrugs ? { backgroundColor: '#f3f4f4' } : {}">
            <div style="border: none; width: 100%; height: 100%;">
                <iframe v-if="htmlContent" :srcdoc="htmlContent" style="width:100%; height:100vh; border:none;"></iframe>
                <div v-else style="display: flex; justify-content: center; align-items: center; height: 100%;">
                    <i class="pi pi-spin pi-spinner" style="font-size: 2rem !important;"></i>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

// The stylesheet never changes, and a document load triggers more than one
// render, so fetch it once per page rather than once per render.
let stylesheetRequest = null;
function loadStylesheet() {
    if (!stylesheetRequest) {
        stylesheetRequest = axios.get('/cda-adha/src/DH_Generic_CDA_Stylesheet-1.7.6.xsl')
            .then(res => res.data)
            .catch(err => {
                // Don't cache a failure - let the next render try again.
                stylesheetRequest = null;
                throw err;
            });
    }
    return stylesheetRequest;
}

function base64ToBytes(b64) {
    return Uint8Array.from(window.atob(b64), c => c.charCodeAt(0));
}

// The payloads are UTF-8, so decode the bytes rather than treating atob's
// output as the string itself.
function base64ToUtf8(b64) {
    return new TextDecoder().decode(base64ToBytes(b64));
}

export default {
    props: ['xml', 'attachments'],
    data() {
        return {
            htmlContent: null,
            // Blob URLs we created for this document, so they can be released.
            objectUrls: [],
            // `xml` and `attachments` both change when a document loads, so
            // two renders start against the same document. Without this, the
            // slower one finishes last and can publish HTML pointing at blob
            // URLs the other render already revoked.
            renderId: 0
        }
    },
    watch: {
        xml: {
            immediate: true,
            handler() {
                this.render();
            }
        },
        attachments() {
            this.render();
        }
    },
    beforeUnmount() {
        this.releaseObjectUrls();
    },
    methods: {
        releaseObjectUrls() {
            this.objectUrls.forEach(url => URL.revokeObjectURL(url));
            this.objectUrls = [];
        },
        // The CDA references package attachments by bare filename, e.g.
        // <reference value="AdvanceCarePlaning.pdf" />, which the stylesheet
        // renders as a relative link. A relative link in a srcdoc iframe
        // resolves against the parent page, so it used to end up pointing at
        // the app itself (/patient/AdvanceCarePlaning.pdf) and 404.
        buildAttachmentUrls() {
            const urls = {};
            (this.attachments || []).forEach(attachment => {
                if (!attachment || !attachment.name || !attachment.content) return;

                try {
                    const blob = new Blob([base64ToBytes(attachment.content)], {
                        type: attachment.mime_type || 'application/octet-stream'
                    });
                    const url = URL.createObjectURL(blob);
                    this.objectUrls.push(url);
                    urls[attachment.name.toLowerCase()] = url;
                } catch (e) {
                    console.error('Could not decode MHR attachment', attachment.name, e);
                }
            });
            return urls;
        },
        resolveAttachmentLinks(doc) {
            const urls = this.buildAttachmentUrls();
            if (!Object.keys(urls).length) return;

            doc.querySelectorAll('[href], [src]').forEach(el => {
                ['href', 'src'].forEach(attr => {
                    const value = el.getAttribute(attr);
                    // Only relative references; leave real URLs and anchors be.
                    if (!value || /^([a-z]+:|\/\/|#)/i.test(value)) return;

                    const fileName = value.split(/[?#]/)[0].split('/').pop().toLowerCase();
                    const url = urls[fileName];
                    if (!url) return;

                    el.setAttribute(attr, url);
                    if (attr === 'href') {
                        // Otherwise the PDF replaces the viewer inside the iframe.
                        el.setAttribute('target', '_blank');
                    }
                });
            });
        },
        render() {
            const source = this.xml;
            if (!source) return;

            const renderId = ++this.renderId;
            this.releaseObjectUrls();
            this.htmlContent = null;

            let xmlString = "";
            if (source.startsWith('data:text/xml;charset=utf-8;base64,')) {
                const b64 = source.split(',')[1];
                xmlString = base64ToUtf8(b64);
            } else if (source.startsWith('data:text/html')) {
                // Pre-rendered HTML (e.g. from Views)
                const b64 = source.split(',')[1];
                this.htmlContent = base64ToUtf8(b64);
                return;
            } else {
                // Not a base64 xml, maybe a URL or other view format?
                // Let's assume it's just raw HTML or we can fallback to standard src
                this.htmlContent = source;
                return;
            }

            // Transform using ADHA stylesheet
            loadStylesheet().then(xsltString => {
                // A newer render has taken over; anything built here would
                // reference blob URLs that render has already revoked.
                if (renderId !== this.renderId) return;

                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(xmlString, "application/xml");
                const xsltDoc = parser.parseFromString(xsltString, "application/xml");

                const processor = new XSLTProcessor();
                processor.importStylesheet(xsltDoc);
                const resultDoc = processor.transformToDocument(xmlDoc);
                this.resolveAttachmentLinks(resultDoc);
                this.htmlContent = new XMLSerializer().serializeToString(resultDoc);
            }).catch(err => {
                if (renderId !== this.renderId) return;
                console.error("Failed to load XSLT", err);
                this.htmlContent = "<p>Error loading stylesheet</p>";
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.dummy-icon {
    width: 50px !important;
}

.administered {
    background-color: #00AD50 !important;
    color: white !important;

    p {
        margin: 0;
        padding: 0;
        font-weight: bold;
        width: fit-content
    }
}

.not-administered {
    background-color: #E04F39 !important;
    color: white !important;

    p {
        margin: 0;
        padding: 0;
        font-weight: bold;
        width: fit-content
    }
}

.medication-group-header {
    h2 {
        font-size: 18px;
    }

    span {
        font-size: 18px;
    }
}

.medication-group {
    table {
        td {
            font-size: 16px;

            p {
                margin: 0;
            }
        }
    }
}

.admin-chart-header {
    display: flex;
    padding: 1em;
    padding-top: 0;
}

.drug-image {
    // height: 75px;
    // width: 75px;
    // background-color: white;
    padding-left: 15px;

    img {
        max-height: 100px;
        max-width: 120px;
        width: auto;
        height: auto;
    }
}

.administer-button {
    box-sizing: border-box;
    width: 187px;
    background: #FFFFFF;
    border: 2px solid #000000;
    border-radius: 6px;
    margin: 0 auto;
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    cursor: pointer;
}

.grey-field {
    background-color: #f3f4f4 !important;
    border: none !important;
}

.four-table-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
}

.four-column-entry {
    width: 22%;
    min-width: 120px;
    margin-right: 10px;
    margin-bottom: 20px;
}

.two-column-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.two-column-entry {
    width: 45%;
    min-width: 200px;
    margin-right: 10px;
}

.medication-group {
    // margin-top: 10px;
    margin-bottom: 10px;

    h2 {
        color: white;
        width: fit-content;
    }

    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}

.packed-medication-row {
    background-color: rgba(8, 112, 220, 0.2);

    td {
        padding: 10px;
    }
}

.scheduled-medication-row {
    background-color: rgba(224, 79, 57, 0.2);

    td {
        padding: 10px;
    }
}

.insulin-medication-row {
    background-color: #FFE67C;

    td {
        padding: 10px;
    }
}

.non-packed-medication-row {
    background-color: #F3F4F4;

    td {
        padding: 10px;
    }
}

.prn-medication-row {
    background-color: #94D6D0;

    td {
        padding: 10px;
    }
}

.nim-medication-row {
    background-color: rgba(39, 65, 136, 0.35);

    td {
        padding: 10px;
    }
}

.patch-medication-row {
    background-color: #FED182;

    td {
        padding: 10px;
    }
}




.pi-check,
.pi-times,
.pi-sign-out,
.pi-lock-open,
.pi-eject,
.pi-replay {
    background-color: white;
    padding: 10px;
    font-weight: bold;
    border-radius: 8px;
    color: grey;
    cursor: pointer;
}



.tag {
    pointer-events: none;
    background-color: white;
    border: none;
    color: black;
    padding: 5px 20px;
    text-align: center;
    text-decoration: none;
    display: inline-block;
    border-radius: 10px;
}
</style>
